<template>
    <div class="p-4 lg:w-full md:w-full">
        <!-- Server Time -->
        <div class="mb-4 p-4 bg-gray-50 border-l-4 border-gray-500 text-gray-700 rounded flex flex-wrap items-center justify-between gap-4">
            <div>
                <b>Waktu Server (GMT+7)</b>
                <div class="text-sm mt-1">
                    <span v-if="!serverTimeError && formattedServerTime">{{ formattedServerTime }}</span>
                    <span v-else-if="serverTimeError">{{ serverTimeError }}</span>
                    <span v-else>Memuat waktu server…</span>
                </div>
            </div>
            <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500" v-if="lastSyncedAt">Sinkron {{ lastSyncedAt }}</span>
                <button
                    type="button"
                    class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                    @click="fetchServerTime"
                    :disabled="loadingServerTime"
                >
                    {{ loadingServerTime ? 'Memuat…' : 'Segarkan' }}
                </button>
            </div>
        </div>
        <!-- Input Method Toggle -->
        <div class="mb-4 p-4 bg-gray-50 border-l-4 border-indigo-500 text-gray-700 rounded flex flex-wrap items-center justify-between gap-4">
            <div>
                <b>Metode Input Jadwal</b>
                <div class="text-sm text-gray-500">Pilih cara pengisian tanggal dan waktu untuk seluruh jadwal</div>
            </div>
            <div class="flex gap-2">
                <button
                    type="button"
                    class="px-3 py-1 text-sm border rounded transition-colors"
                    :class="inputMethod === 'picker'
                        ? 'border-indigo-500 bg-indigo-500 text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'"
                    @click="setInputMethod('picker')"
                >
                    Date Picker
                </button>
                <button
                    type="button"
                    class="px-3 py-1 text-sm border rounded transition-colors"
                    :class="inputMethod === 'manual'
                        ? 'border-indigo-500 bg-indigo-500 text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'"
                    @click="setInputMethod('manual')"
                >
                    Manual Entry
                </button>
            </div>
        </div>
        <!-- Tips Jadwal Constraint -->
        <div class="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-700 rounded">
            <b>Tips Penjadwalan:</b>
            <ul class="list-disc ml-6 mt-2 text-sm">
                <li>Jadwal <b>Download Dokumen Pemilihan</b> dimulai <b>1 hari setelah</b> <b>Pengumuman Pengadaan</b></li>
                <li>Jadwal <b>Penjelasan Pemilihan</b> minimal <b>3 hari setelah</b> <b>Pengumuman Pengadaan</b></li>
                <li>Jadwal <b>Upload Dokumen Penawaran</b> minimal <b>berakhir 7 hari setelah</b> <b>Penjelasan Pengadaan</b></li>
                <li>Jadwal <b>Penetapan Pemenang</b> minimal <b>berlangsung selama 3 hari</b></li>
                <li>Jadwal <b>Masa Sanggah</b> <b>berlangsung selama 3 hari</b> dan <b>dimulai bersama</b> dengan <b>Pengumuman Pemenang</b></li>
                <li>Jadwal <b>Surat Penunjukan Penyedia Barang/Jasa</b> minimal <b>dimulai 3 hari setelah</b> <b>Pengumuman Pemenang</b> dan <b>maksimal 7 hari</b></li>
                <li>Jadwal <b>Penandatanganan Kontrak</b> <b>maksimal dimulai 14 hari</b> dari <b>Surat Penunjukan Penyedia Barang/Jasa</b></li>
            </ul>
        </div>
        <!-- Constraint Warning -->
        <div v-if="constraintViolations.length > 0" class="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700 rounded">
            <b>Peringatan Jadwal:</b>
            <ul class="list-disc ml-6 mt-2 text-sm">
                <li v-for="(msg, idx) in constraintViolations" :key="idx">{{ msg }}</li>
            </ul>
        </div>
        <div class="space-y-4">
            <div
                class="field w-full mb-3"
                v-for="(jadwal, index) in localJadwals"
                :key="jadwal.kode_jadwal || index"
            >
                <div class="flex items-center w-full">
                    <label class="text-gray-700 font-bold mr-2 w-1/4">
                        {{ jadwal.nama_jadwal }}
                    </label>
                    <div v-if="inputMethod === 'picker'" class="flex gap-2 w-3/4">
                        <div class="flex flex-col w-1/3">
                            <div class="flex items-center">
                                <label for="waktu_mulai" class="block mr-2"
                                    >Mulai</label
                                >
                                <Calendar
                                    :id="'waktu_mulai_' + index"
                                    v-model="jadwal.existing_jadwal.waktu_mulai"
                                    showTime
                                    dateFormat="yy-mm-dd"
                                    hourFormat="24"
                                    class="w-full"
                                    :maxDate="jadwal.existing_jadwal.waktu_selesai"
                                    :manualInput="true"
                                    :showIcon="true"
                                    @date-select="onDateChange(index)"
                                    @update:modelValue="onDateChange(index, 'picker')"
                                />
                            </div>
                            <small class="text-red-500" v-if="jadwal.errors?.waktu_mulai">
                                {{ jadwal.errors.waktu_mulai }}
                            </small>
                        </div>
                        <div class="flex flex-col w-1/3">
                            <div class="flex items-center">
                                <label for="waktu_selesai" class="block mr-2"
                                    >Selesai</label
                                >
                                <Calendar
                                    :id="'waktu_selesai_' + index"
                                    v-model="jadwal.existing_jadwal.waktu_selesai"
                                    showTime
                                    dateFormat="yy-mm-dd"
                                    hourFormat="24"
                                    class="w-full"
                                    :minDate="jadwal.existing_jadwal.waktu_mulai"
                                    :manualInput="true"
                                    :showIcon="true"
                                    @date-select="onDateChange(index)"
                                    @update:modelValue="onDateChange(index, 'picker')"
                                />
                            </div>
                            <small class="text-red-500" v-if="jadwal.errors?.waktu_selesai">
                                {{ jadwal.errors.waktu_selesai }}
                            </small>
                        </div>
                        <div class="flex items-center w-1/3">
                            <label for="keterangan" class="block mr-2"
                                >Keterangan</label
                            >
                            <Input
                                :id="'keterangan_' + index"
                                v-model="jadwal.existing_jadwal.keterangan"
                                type="text"
                                class="w-full"
                                @input="onDateChange(index)"
                            />
                            <small class="text-red-500 ml-2" v-if="jadwal.errors?.keterangan">
                                {{ jadwal.errors.keterangan }}
                            </small>
                        </div>
                    </div>
                    <div v-else class="w-3/4">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div class="flex flex-col">
                                <label class="text-gray-700 font-bold mb-1">Mulai</label>
                                <input
                                    :id="'manual_waktu_mulai_' + index"
                                    type="text"
                                    class="p-inputtext p-component w-full"
                                    placeholder="yyyy-mm-dd HH:mm"
                                    :value="manualValues[index]?.waktu_mulai || ''"
                                    @input="onManualInput(index, 'waktu_mulai', $event.target.value)"
                                />
                                <small class="text-red-500" v-if="jadwal.errors?.waktu_mulai">
                                    {{ jadwal.errors.waktu_mulai }}
                                </small>
                            </div>
                            <div class="flex flex-col">
                                <label class="text-gray-700 font-bold mb-1">Selesai</label>
                                <input
                                    :id="'manual_waktu_selesai_' + index"
                                    type="text"
                                    class="p-inputtext p-component w-full"
                                    placeholder="yyyy-mm-dd HH:mm"
                                    :value="manualValues[index]?.waktu_selesai || ''"
                                    @input="onManualInput(index, 'waktu_selesai', $event.target.value)"
                                />
                                <small class="text-red-500" v-if="jadwal.errors?.waktu_selesai">
                                    {{ jadwal.errors.waktu_selesai }}
                                </small>
                            </div>
                        </div>
                        <div class="flex flex-col mt-3">
                            <label class="text-gray-700 font-bold mb-1">Keterangan</label>
                            <Input
                                :id="'keterangan_manual_' + index"
                                v-model="jadwal.existing_jadwal.keterangan"
                                type="text"
                                class="w-full"
                                @input="onDateChange(index)"
                            />
                            <small class="text-red-500" v-if="jadwal.errors?.keterangan">
                                {{ jadwal.errors.keterangan }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, watch, computed, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import Fieldset from "primevue/fieldset";
import Input from "@/Components/Input";
import Calendar from "primevue/calendar";

export default defineComponent({
    components: {
        Fieldset,
        Input,
        Calendar,
    },
    props: {
        jadwals: {
            type: Array,
            required: true,
        },
    },
    setup(props, { emit }) {
        const localJadwals = ref([]);
        // Constraint violation messages
        const constraintViolations = ref([]);
        // Boolean: apakah constraint terpenuhi
        const jadwalConstraintValid = ref(true);

        // Store watcher references for cleanup
        const watchers = [];
        const inputMethod = ref('picker');

        // Server time state
        const serverTime = ref(null);
        const loadingServerTime = ref(false);
        const serverTimeError = ref('');
        const lastFetch = ref(null);
        let tickTimer = null;
        let refreshTimer = null;

        const formattedServerTime = computed(() => {
            if (!serverTime.value) {
                return '';
            }

            return new Intl.DateTimeFormat('id-ID', {
                timeZone: 'Asia/Jakarta',
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            }).format(serverTime.value);
        });

        const lastSyncedAt = computed(() => {
            if (!lastFetch.value) {
                return '';
            }

            return new Intl.DateTimeFormat('id-ID', {
                timeZone: 'Asia/Jakarta',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            }).format(lastFetch.value);
        });

        const fetchServerTime = async () => {
            if (loadingServerTime.value) {
                return;
            }

            loadingServerTime.value = true;
            serverTimeError.value = '';

            try {
                const response = await axios.get('/api/server-time');
                const payload = response?.data?.data;

                if (payload?.iso) {
                    serverTime.value = new Date(payload.iso);
                    lastFetch.value = new Date();
                } else if (payload?.timestamp) {
                    serverTime.value = new Date(payload.timestamp * 1000);
                    lastFetch.value = new Date();
                } else {
                    serverTime.value = null;
                    serverTimeError.value = 'Server time tidak tersedia.';
                }
            } catch (error) {
                console.error('Failed to fetch server time', error);
                serverTime.value = null;
                serverTimeError.value = 'Gagal memuat waktu server.';
            } finally {
                loadingServerTime.value = false;
            }
        };

        const manualValues = ref([]);
        const rawManualTouched = ref([]);

        const toDate = (value) => {
            if (!value) {
                return null;
            }

            if (value instanceof Date) {
                return value;
            }

            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        };

        const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

        const isValidDate = (value) => value instanceof Date && !Number.isNaN(value?.getTime?.());

        const formatManualDateTime = (value) => {
            if (!isValidDate(value)) {
                return '';
            }

            const pad = (num) => String(num).padStart(2, '0');
            const year = value.getFullYear();
            const month = pad(value.getMonth() + 1);
            const day = pad(value.getDate());
            const hour = pad(value.getHours());
            const minute = pad(value.getMinutes());

            return `${year}-${month}-${day} ${hour}:${minute}`;
        };

        const ensureManualEntry = (index) => {
            if (!manualValues.value[index]) {
                manualValues.value[index] = {
                    waktu_mulai: '',
                    waktu_selesai: '',
                };
            }

            if (!rawManualTouched.value[index]) {
                rawManualTouched.value[index] = {
                    waktu_mulai: false,
                    waktu_selesai: false,
                };
            }

            return manualValues.value[index];
        };

        const syncManualFromDate = (index) => {
            const entry = localJadwals.value[index];
            if (!entry) {
                return;
            }

            const manualEntry = ensureManualEntry(index);

            manualEntry.waktu_mulai = formatManualDateTime(entry.existing_jadwal?.waktu_mulai);
            manualEntry.waktu_selesai = formatManualDateTime(entry.existing_jadwal?.waktu_selesai);
        };

        const parseManualDateTime = (value) => {
            if (!value || typeof value !== 'string') {
                return null;
            }

            const trimmed = value.trim();
            const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/);
            if (!match) {
                return null;
            }

            const [ , year, month, day, hour, minute ] = match;
            const isoString = `${year}-${month}-${day}T${hour}:${minute}:00`;
            const parsed = new Date(isoString);

            return isValidDate(parsed) ? parsed : null;
        };

        const onManualInput = (index, field, value) => {
            ensureManualEntry(index);
            rawManualTouched.value[index][field] = value.trim().length > 0;
            manualValues.value[index][field] = value;

            const parsed = parseManualDateTime(value);
            const jadwal = localJadwals.value[index];

            if (jadwal) {
                jadwal.existing_jadwal[field] = parsed;
                onDateChange(index, 'manual');
            }
        };

        const initializeJadwals = (sourceJadwals = []) => {
            const existingMap = new Map(
                (localJadwals.value || []).map((item, index) => [item.kode_jadwal ?? `idx_${index}`, { index, item }])
            );

            const previousManualMap = new Map(
                (manualValues.value || []).map((entry, index) => [
                    (localJadwals.value?.[index]?.kode_jadwal) ?? `idx_${index}`,
                    entry,
                ])
            );

            const mapped = (sourceJadwals || []).map((jadwal, index) => {
                const key = jadwal?.kode_jadwal ?? `idx_${index}`;
                const previousRecord = existingMap.get(key);
                const previous = previousRecord?.item;
                const incomingExisting = jadwal?.existing_jadwal || {};

                if (previous) {
                    const previousExisting = previous.existing_jadwal || {};
                    const startDate = toDate(incomingExisting.waktu_mulai);
                    const endDate = toDate(incomingExisting.waktu_selesai);

                    return {
                        ...jadwal,
                        existing_jadwal: {
                            ...incomingExisting,
                            ...previousExisting,
                            waktu_mulai: hasOwn(previousExisting, 'waktu_mulai')
                                ? previousExisting.waktu_mulai
                                : startDate,
                            waktu_selesai: hasOwn(previousExisting, 'waktu_selesai')
                                ? previousExisting.waktu_selesai
                                : endDate,
                            keterangan: hasOwn(previousExisting, 'keterangan')
                                ? previousExisting.keterangan
                                : incomingExisting.keterangan ?? '',
                            tempat: hasOwn(previousExisting, 'tempat')
                                ? previousExisting.tempat
                                : incomingExisting.tempat ?? '',
                        },
                        errors: previous.errors ? { ...previous.errors } : {},
                    };
                }

                const startDate = toDate(incomingExisting.waktu_mulai);
                const endDate = toDate(incomingExisting.waktu_selesai);

                return {
                    ...jadwal,
                    existing_jadwal: {
                        waktu_mulai: startDate,
                        waktu_selesai: endDate,
                        keterangan: incomingExisting.keterangan || '',
                        tempat: incomingExisting.tempat || '',
                    },
                    errors: {},
                };
            });

            localJadwals.value = mapped;

            manualValues.value = mapped.map((entry, idx) => {
                const key = entry.kode_jadwal ?? `idx_${idx}`;
                const previousManual = previousManualMap.get(key) || manualValues.value?.[idx] || {};
                const synced = {
                    waktu_mulai: formatManualDateTime(entry.existing_jadwal?.waktu_mulai),
                    waktu_selesai: formatManualDateTime(entry.existing_jadwal?.waktu_selesai),
                };

                return {
                    waktu_mulai: previousManual.waktu_mulai ?? synced.waktu_mulai,
                    waktu_selesai: previousManual.waktu_selesai ?? synced.waktu_selesai,
                };
            });
        };

        const getJadwalByKode = (kode) => localJadwals.value.find(j => j.kode_jadwal === kode);
        // Constraint logic
        const checkConstraints = () => {
            const v = [];
            // Helper: get start/end as Date
            const getStart = (kode) => {
                const j = getJadwalByKode(kode);
                const value = j?.existing_jadwal?.waktu_mulai;
                return isValidDate(value) ? value : null;
            };
            const getEnd = (kode) => {
                const j = getJadwalByKode(kode);
                const value = j?.existing_jadwal?.waktu_selesai;
                return isValidDate(value) ? value : null;
            };
            // 1. download_dokumen_pemilihan starts 1 day after pengumuman_pengadaan
            const dwn = getStart('download_dokumen_pemilihan');
            const pengumuman = getStart('pengumuman_pengadaan');
            if (dwn && pengumuman) {
                const diff = (dwn - pengumuman) / (1000*60*60*24);
                if (diff < 1) v.push('Download Dokumen Pemilihan harus dimulai minimal 1 hari setelah Pengumuman Pengadaan.');
            }
            // 2. penjelasan_pemilihan minimal 3 hari setelah pengumuman_pengadaan
            const penjelasan = getStart('penjelasan_pemilihan');
            if (penjelasan && pengumuman) {
                const diff = (penjelasan - pengumuman) / (1000*60*60*24);
                if (diff < 3) v.push('Penjelasan Pemilihan minimal 3 hari setelah Pengumuman Pengadaan.');
            }
            // 3. upload_dokumen_penawaran minimal berakhir 7 hari setelah penjelasan_pengadaan
            const uploadEnd = getEnd('upload_dokumen_penawaran');
            const penjelasanEnd = getEnd('penjelasan_pengadaan');
            if (uploadEnd && penjelasanEnd) {
                const diff = (uploadEnd - penjelasanEnd) / (1000*60*60*24);
                if (diff < 7) v.push('Upload Dokumen Penawaran harus berakhir minimal 7 hari setelah Penjelasan Pengadaan.');
            }
            // 4. penetapan_pemenang minimal berlangsung 3 hari
            const penetapanStart = getStart('penetapan_pemenang');
            const penetapanEnd = getEnd('penetapan_pemenang');
            if (penetapanStart && penetapanEnd) {
                const diff = (penetapanEnd - penetapanStart) / (1000*60*60*24);
                if (diff < 3) v.push('Penetapan Pemenang minimal berlangsung selama 3 hari.');
            }
            // 5. masa_sanggah berlangsung 3 hari dan dimulai bersama pengumuman_pemenang
            const masaSanggahStart = getStart('masa_sanggah');
            const masaSanggahEnd = getEnd('masa_sanggah');
            const pengumumanPemenangStart = getStart('pengumuman_pemenang');
            if (masaSanggahStart && pengumumanPemenangStart) {
                if (Math.abs(masaSanggahStart - pengumumanPemenangStart) > 1000*60*60*2) v.push('Masa Sanggah harus dimulai bersamaan dengan Pengumuman Pemenang.');
            }
            if (masaSanggahStart && masaSanggahEnd) {
                const diff = (masaSanggahEnd - masaSanggahStart) / (1000*60*60*24);
                if (Math.abs(diff - 3) > 0.1) v.push('Masa Sanggah harus berlangsung selama 3 hari.');
            }
            // 6. surat_penunjukan_penyedia_barang_jasa minimal dimulai 3 hari setelah pengumuman_pemenang dan maksimal 7 hari
            const sppbjStart = getStart('surat_penunjukan_penyedia_barang_jasa');
            if (sppbjStart && pengumumanPemenangStart) {
                const diff = (sppbjStart - pengumumanPemenangStart) / (1000*60*60*24);
                if (diff < 3) v.push('Surat Penunjukan Penyedia Barang/Jasa minimal dimulai 3 hari setelah Pengumuman Pemenang.');
                if (diff > 7) v.push('Surat Penunjukan Penyedia Barang/Jasa maksimal dimulai 7 hari setelah Pengumuman Pemenang.');
            }
            // 7. surat_penunjukan_penyedia_barang_jasa berlangsung maksimal 7 hari
            const sppbjEnd = getEnd('surat_penunjukan_penyedia_barang_jasa');
            if (sppbjStart && sppbjEnd) {
                const diff = (sppbjEnd - sppbjStart) / (1000*60*60*24);
                if (diff > 7) v.push('Surat Penunjukan Penyedia Barang/Jasa maksimal berlangsung selama 7 hari.');
            }
            // 8. penandatanganan_kontrak dimulai maksimal 14 hari dari surat_penunjukan_penyedia_barang_jasa
            const kontrakStart = getStart('penandatanganan_kontrak');
            if (kontrakStart && sppbjStart) {
                const diff = (kontrakStart - sppbjStart) / (1000*60*60*24);
                if (diff > 14) v.push('Penandatanganan Kontrak harus dimulai maksimal 14 hari dari Surat Penunjukan Penyedia Barang/Jasa.');
            }
            constraintViolations.value = v;
            jadwalConstraintValid.value = v.length === 0;
            emit('jadwal-constraint-valid', jadwalConstraintValid.value);
        };

        const validateDates = (index) => {
            const jadwal = localJadwals.value[index];
            if (!jadwal) {
                return;
            }

            jadwal.errors = {};

            const startDate = jadwal.existing_jadwal.waktu_mulai;
            const endDate = jadwal.existing_jadwal.waktu_selesai;
            const startValid = isValidDate(startDate);
            const endValid = isValidDate(endDate);
            const description = (jadwal.existing_jadwal.keterangan || '').trim();

            const manualEntry = manualValues.value[index] || { waktu_mulai: '', waktu_selesai: '' };
            const manualMulaiLength = manualEntry.waktu_mulai?.trim().length || 0;
            const manualSelesaiLength = manualEntry.waktu_selesai?.trim().length || 0;
            const manualMulaiComplete = manualMulaiLength >= 16; // yyyy-mm-dd HH:mm
            const manualSelesaiComplete = manualSelesaiLength >= 16;

            if (!startValid && (manualMulaiComplete || startDate !== null)) {
                jadwal.errors.waktu_mulai = manualMulaiComplete
                    ? 'Format atau nilai waktu mulai tidak valid'
                    : 'Format atau nilai waktu mulai tidak valid';
            }

            if (!endValid && (manualSelesaiComplete || endDate !== null)) {
                jadwal.errors.waktu_selesai = manualSelesaiComplete
                    ? 'Format atau nilai waktu selesai tidak valid'
                    : 'Format atau nilai waktu selesai tidak valid';
            }

            if (startValid && endValid) {
                if (startDate > endDate) {
                    jadwal.errors.waktu_mulai = 'Waktu mulai harus sebelum waktu selesai';
                    jadwal.errors.waktu_selesai = 'Waktu selesai harus setelah waktu mulai';
                }
            }

            if (description.length < 30) {
                jadwal.errors.keterangan = 'Keterangan wajib diisi minimal 30 karakter.';
            }

            emit('update:jadwals', localJadwals.value);
            emit('validation', isValid());
            checkConstraints();
        };

        const isValid = () => {
            return localJadwals.value.every(jadwal => {
                if (!jadwal.errors) {
                    return true;
                }
                return Object.keys(jadwal.errors).length === 0;
            });
        };

        const onDateChange = (index, source = 'picker') => {
            validateDates(index);

            if (source !== 'manual') {
                syncManualFromDate(index);
            }
        };

        const setInputMethod = (method) => {
            if (method !== 'picker' && method !== 'manual') {
                return;
            }
            inputMethod.value = method;
        };

        // Setup watchers with cleanup tracking
        const propsWatcher = watch(
            () => props.jadwals,
            (newVal) => {
                initializeJadwals(Array.isArray(newVal) ? newVal : []);
                checkConstraints();
            },
            { immediate: true, deep: true }
        );
        
        // Add debouncing to prevent excessive calls
        let debounceTimer = null;
        const deepWatcher = watch(localJadwals, () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            debounceTimer = setTimeout(() => {
                checkConstraints();
                emit('update:jadwals', localJadwals.value);
            }, 300); // Increased debounce to 300ms
        }, { deep: true });

        const inputMethodWatcher = watch(inputMethod, (method) => {
            if (method === 'manual') {
                localJadwals.value.forEach((_, idx) => syncManualFromDate(idx));
            }
        });

        // Store watchers for cleanup
        watchers.push(propsWatcher, deepWatcher, inputMethodWatcher);

        onMounted(() => {
            fetchServerTime();

            tickTimer = window.setInterval(() => {
                if (serverTime.value) {
                    serverTime.value = new Date(serverTime.value.getTime() + 1000);
                }
            }, 1000);

            refreshTimer = window.setInterval(() => {
                fetchServerTime();
            }, 60000);
        });

        // Cleanup function
        onBeforeUnmount(() => {
            // Stop all watchers
            watchers.forEach(watcher => {
                if (typeof watcher === 'function') {
                    watcher();
                }
            });

            if (tickTimer) {
                clearInterval(tickTimer);
            }

            if (refreshTimer) {
                clearInterval(refreshTimer);
            }
        });

        return {
            localJadwals,
            onDateChange,
            constraintViolations,
            jadwalConstraintValid,
            manualValues,
            formattedServerTime,
            loadingServerTime,
            serverTimeError,
            fetchServerTime,
            lastSyncedAt,
            inputMethod,
            setInputMethod,
            onManualInput,
        };
    },
    emits: ['update:jadwals', 'validation', 'jadwal-constraint-valid'],
});
</script>
